import { useEffect } from "react"
import { Navigate, Outlet, useNavigate } from "react-router-dom"
import { UserRole, UserStatus } from "~/__generated__/graphql"
import {
  contactsPath,
  loginPath,
  onboardingAccountDetailsPath,
  onboardingVerifyEmailPath,
  onboardingVerifyPhonePath,
} from "~/common/paths"
import { useViewerMaybe } from "./use-viewer"

export const RequireUserSignedOut = () => {
  const navigate = useNavigate()
  const { viewer, result } = useViewerMaybe()

  useEffect(() => {
    if (viewer) {
      navigate(contactsPath({}))
    }
  }, [viewer, navigate])

  if (result.loading) return null

  return <Outlet />
}

export const RequireUserActive: React.FC = () => {
  const navigate = useNavigate()
  const { viewer, result } = useViewerMaybe()

  useEffect(() => {
    if (!result.loading && !viewer) {
      navigate(loginPath({}) + "?" + new URLSearchParams({ returnTo: window.location.pathname }))
    }
  }, [navigate, result.loading, viewer])

  if (result.loading) return null
  if (!viewer) return null

  switch (viewer.userStatus) {
    case UserStatus.OnboardingEmailPending:
      return <Navigate to={onboardingVerifyEmailPath({})} replace />
    case UserStatus.OnboardingPhonePending:
      return <Navigate to={onboardingVerifyPhonePath({})} replace />
    case UserStatus.OnboardingAccountDetailsPending:
      return <Navigate to={onboardingAccountDetailsPath({})} replace />
    case UserStatus.Active:
      return <Outlet />
    case UserStatus.Blocked:
    case UserStatus.Invited:
      throw new Error("TODO")
    // they are being logged out
    case UserStatus.ScheduledForDeletion:
      return null
    default:
      throw viewer.userStatus satisfies never
  }
}

export const RequireUserOnboarding: React.FC = () => {
  const { viewer, result } = useViewerMaybe()

  if (result.loading) return null

  if (!viewer) {
    return (
      <Navigate
        to={loginPath({}) + "?" + new URLSearchParams({ returnTo: window.location.pathname })}
        replace
      />
    )
  }

  const isOnboardingStatus =
    viewer.userStatus === UserStatus.OnboardingEmailPending ||
    viewer.userStatus === UserStatus.OnboardingPhonePending ||
    viewer.userStatus === UserStatus.OnboardingAccountDetailsPending

  if (!isOnboardingStatus) {
    return <Navigate to={contactsPath({})} replace />
  }

  return <Outlet />
}

export const RequireSystemAdminSignedIn = () => {
  const navigate = useNavigate()
  const { viewer, result } = useViewerMaybe()

  useEffect(() => {
    if (viewer?.userRole !== UserRole.SystemAdmin) {
      navigate(contactsPath({}))
    }
  }, [navigate, result.loading, viewer])

  if (result.loading) return null

  return <Outlet />
}
